import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import { Calendar as CalendarIcon, DollarSign, ShoppingCart, Receipt, PiggyBank, CreditCard, ChevronDown, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

interface CashFlowData {
  gross_sales: number;
  products_sold: number;
  total_expenses: number;
  gross_profit: number;
  payment_distribution: { mode: string; amount: number }[];
  cogs: number;
  stock_adjustment: number;
  operating_expenses: number;
  operating_profit: number;
  capital_expenses: number;
  net_profit: number;
  total_outflow: number;
}

export default function CashFlowDashboardSection() {
  const [dateStr, setDateStr] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  const { data } = useQuery({
    queryKey: ['cashFlowDashboard', dateStr],
    queryFn: async () => {
      const res = await reportsApi.getCashFlowDashboard(dateStr);
      return res.data.message as CashFlowData;
    },
  });

  const d = data || {
    gross_sales: 0,
    products_sold: 0,
    total_expenses: 0,
    gross_profit: 0,
    payment_distribution: [],
    cogs: 0,
    stock_adjustment: 0,
    operating_expenses: 0,
    operating_profit: 0,
    capital_expenses: 0,
    net_profit: 0,
    total_outflow: 0
  };

  return (
    <div className="space-y-6">
      {/* Date Picker Bar */}
      <div className="flex justify-end">
        <div className="relative">
          <input 
            type="date"
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <CalendarIcon className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {/* 4 Top Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex justify-between items-start">
          <div>
            <p className="text-xs font-bold text-gray-500 tracking-wider mb-2 uppercase">Gross Sales</p>
            <p className="text-2xl font-bold text-gray-900">{formatMoney(d.gross_sales)}</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex justify-between items-start">
          <div>
            <p className="text-xs font-bold text-gray-500 tracking-wider mb-2 uppercase">Products Sold</p>
            <p className="text-2xl font-bold text-gray-900">{d.products_sold}</p>
            <p className="text-xs text-gray-400 mt-1 uppercase">Total Items Sold</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <ShoppingCart className="w-5 h-5 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex justify-between items-start">
          <div>
            <p className="text-xs font-bold text-gray-500 tracking-wider mb-2 uppercase">Total Expenses</p>
            <p className="text-2xl font-bold text-gray-900">{formatMoney(d.total_expenses)}</p>
            <p className="text-xs text-gray-400 mt-1 uppercase leading-tight">Operating, Capital, and<br/>Stock Adjustment Loss</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Receipt className="w-5 h-5 text-orange-600" />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex justify-between items-start">
          <div>
            <p className="text-xs font-bold text-gray-500 tracking-wider mb-2 uppercase">Gross Profit</p>
            <p className="text-2xl font-bold text-gray-900">{formatMoney(d.gross_profit)}</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <PiggyBank className="w-5 h-5 text-emerald-600" />
          </div>
        </div>
      </div>

      {/* 3 Main Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Payment Distribution */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Payment Distribution</h3>
              <p className="text-sm text-gray-500 mt-1">Revenue breakdown by channel</p>
            </div>
            <CreditCard className="w-5 h-5 text-gray-700" />
          </div>
          {d.payment_distribution.length === 0 ? (
            <p className="text-sm text-gray-500">No sales for this day.</p>
          ) : (
            <div className="space-y-4">
              {d.payment_distribution.map((p) => (
                <div key={p.mode} className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">{p.mode}</span>
                  <span className="text-sm font-medium text-gray-900">{formatMoney(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Profit & Loss Flow */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-6">Profit & Loss Flow</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-gray-700 uppercase">Gross Sales</span>
              <span className="text-sm font-bold text-gray-900">{formatMoney(d.gross_sales)}</span>
            </div>
            <div className="flex justify-between items-center text-gray-500 pl-4">
              <span className="text-sm">— Cost of Goods (COGS)</span>
              <span className="text-sm">{formatMoney(d.cogs)}</span>
            </div>
            <div className="flex justify-between items-center pt-2">
              <span className="text-sm font-bold text-green-600 uppercase flex items-center gap-1">Gross Profit</span>
              <span className="text-sm font-bold text-green-600">{formatMoney(d.gross_profit)}</span>
            </div>
            <div className="flex justify-between items-center text-gray-500 pl-4 pt-2">
              <span className="text-sm">— Stock adjustment (inventory loss)</span>
              <span className="text-sm">{formatMoney(d.stock_adjustment)}</span>
            </div>
            <div className="flex justify-between items-center text-gray-500 pl-4">
              <span className="text-sm">— Operating Expenses</span>
              <span className="text-sm">{formatMoney(d.operating_expenses)}</span>
            </div>
            
            <div className="border-t border-dashed border-gray-200 my-2"></div>

            <div className="flex justify-between items-center pt-1">
              <span className="text-sm font-bold text-green-600 uppercase flex items-center gap-1">Operating Profit</span>
              <span className="text-sm font-bold text-green-600">{formatMoney(d.operating_profit)}</span>
            </div>
            <div className="flex justify-between items-center text-gray-500 pl-4 pt-2">
              <span className="text-sm">— Capital Expenditures</span>
              <span className="text-sm">{formatMoney(d.capital_expenses)}</span>
            </div>

            <div className="mt-4 border border-green-500 bg-green-50 rounded-lg p-4 flex justify-between items-center">
              <span className="text-sm font-bold text-green-700 uppercase flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Net Profit
              </span>
              <span className="text-sm font-bold text-green-700">{formatMoney(d.net_profit)}</span>
            </div>
          </div>
        </div>

        {/* Expense Breakdown */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-6">Expense Breakdown</h3>
          
          <div className="space-y-6">
            <div className="flex justify-between items-start">
              <div className="flex flex-col">
                <span className="text-sm font-bold text-gray-800 flex items-center gap-1">
                  <ChevronDown className="w-4 h-4" /> Cost of Goods Sold
                </span>
                <span className="text-xs text-gray-400 pl-5 uppercase mt-0.5">Recorded</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{formatMoney(d.cogs)}</span>
            </div>

            <div className="flex justify-between items-start">
              <div className="flex flex-col">
                <span className="text-sm font-bold text-gray-800 flex items-center gap-1">
                  <ChevronDown className="w-4 h-4" /> Stock adjustment (inventory loss)
                </span>
                <span className="text-xs text-gray-400 pl-5 uppercase mt-0.5">Recorded</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{formatMoney(d.stock_adjustment)}</span>
            </div>

            <div className="flex justify-between items-start">
              <div className="flex flex-col">
                <span className="text-sm font-bold text-gray-800 flex items-center gap-1">
                  <ChevronDown className="w-4 h-4" /> Operating Expenses
                </span>
                <span className="text-xs text-gray-400 pl-5 uppercase mt-0.5">Recorded</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{formatMoney(d.operating_expenses)}</span>
            </div>

            <div className="flex justify-between items-start">
              <div className="flex flex-col">
                <span className="text-sm font-bold text-gray-800 flex items-center gap-1">
                  <ChevronDown className="w-4 h-4" /> Capital Expenses
                </span>
                <span className="text-xs text-gray-400 pl-5 uppercase mt-0.5">Recorded</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{formatMoney(d.capital_expenses)}</span>
            </div>

            <div className="mt-6 border border-red-500 bg-red-50 rounded-lg p-4 flex justify-between items-center">
              <span className="text-sm font-bold text-red-600 uppercase flex items-center gap-2">
                <Receipt className="w-4 h-4" /> Total Outflow
              </span>
              <span className="text-sm font-bold text-red-600">{formatMoney(d.total_outflow)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
